import * as React from "react";
import * as _ from "underscore";
import { connect } from "react-redux";
import { Button } from "carbon-components-react";
import { CloudDownload16, Save16, DocumentView24 } from "@carbon/icons-react";

import { AppShape, HurdleShape, DocTypes } from "@eave/shared/types/api";
import HurdleView from "underwriter_dashboard/shared/HurdleView";

import { getFileDownloadBaseURL } from "../../../config";
import APIClient from "../../APIClient";
import { loadApp } from "../actions";
import { HurdleStatus } from "../HurdleStatus";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");

require(`../../../assets/stylesheets/components/underwriter_dashboard/hurdle_editors/\
subject_property_report.scss`);

const classnames = require("classnames");

interface EditorProps {
  dispatch: Function;
  app: AppShape;
  hurdle: HurdleShape;
  onHide: Function;
}

class Editor extends React.Component<EditorProps, any> {
  constructor(props) {
    super(props);
    this.state = {
      fetching: false,
      subjPropChecks: null,
      expanded: [],
    };
  }

  componentDidMount() {
    this.loadSubjPropChecks(this.props.app.id);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.app.id !== this.props.app.id) {
      this.loadSubjPropChecks(nextProps.app.id);
    }
  }

  loadSubjPropChecks(appId) {
    if (!this.getMostRecentDocOfType(this.props.app, DocTypes.SubjectPropertyReport)) {
      return; // no need to pull checks if no subject property report yet
    }

    // TODO: jjw: this should really go in redux... :-/
    APIClient.get(`/loan_applications/${appId}/hurdles/subject_property_report/checks`).then(
      (resp: any) => {
        this.setState({
          ...this.state,
          subjPropChecks: resp.checks && resp.checks.length ? resp.checks : null,
        });
      },
    );
  }

  reloadApp() {
    loadApp(this.props.dispatch, this.props.app.id);
  }

  requestReports() {
    const { id } = this.props.app;

    this.setState({
      ...this.state,
      fetching: true,
    });

    // TODO: jjw: this should really go in redux... :-/
    Promise.all([
      APIClient.get(`/loan_applications/${id}/subject_property`),
      APIClient.get(`/loan_applications/${id}/transaction_history`),
    ]).then(() => {
      this.setState(
        {
          ...this.state,
          fetching: false,
        },
        () => {
          this.reloadApp(); // reload to get updated report data
        },
      );
    });
  }

  needToCreateFlags() {
    const { subjPropChecks } = this.state;

    if (!subjPropChecks || !subjPropChecks.length) return false;

    const last24Months: any = _.findWhere(subjPropChecks, {
      name: "names_do_not_appear_in_last_24_months",
    });

    return last24Months.sufficient_info && !last24Months.passed;
  }

  createFlag(message) {
    const { app, hurdle } = this.props;

    return APIClient.post("/flags", {
      message,
      application_id: app.id,
      hurdle_name: hurdle.name,
    });
  }

  createFlags(): any {
    const { subjPropChecks } = this.state;

    if (!subjPropChecks || !subjPropChecks.length) return Promise.resolve();

    const last24Months: any = _.findWhere(subjPropChecks, {
      name: "names_do_not_appear_in_last_24_months",
    });

    const toCreate = [];

    if (last24Months.sufficient_info && !last24Months.passed) {
      toCreate.push(this.createFlag(last24Months.explanation));
    }

    return Promise.all(toCreate);
  }

  needToCreateAANs() {
    const { subjPropChecks } = this.state;

    if (!subjPropChecks || !subjPropChecks.length) return false;

    const last12Months: any = _.findWhere(subjPropChecks, {
      name: "sold_twice_in_last_12_months",
    });
    const last3Months: any = _.findWhere(subjPropChecks, {
      name: "sold_in_last_3_months",
    });
    const foreclosure: any = _.findWhere(subjPropChecks, {
      name: "no_more_than_1_foreclosure_in_last_24_months",
    });

    return (
      (last12Months.sufficient_info && !last12Months.passed) ||
      (last3Months.sufficient_info && !last3Months.passed) ||
      (foreclosure.sufficient_info && !foreclosure.passed)
    );
  }

  createAAN(reason) {
    return APIClient.post(`/${this.props.app.id}/aans`, { reasons: [reason] });
  }

  createAANs(): any {
    const { subjPropChecks } = this.state;

    if (!subjPropChecks || !subjPropChecks.length) return Promise.resolve();

    const last12Months: any = _.findWhere(subjPropChecks, {
      name: "sold_twice_in_last_12_months",
    });
    const last3Months: any = _.findWhere(subjPropChecks, {
      name: "sold_in_last_3_months",
    });
    const foreclosure: any = _.findWhere(subjPropChecks, {
      name: "no_more_than_1_foreclosure_in_last_24_months",
    });

    const toCreate = [];

    if (last12Months.sufficient_info && !last12Months.passed) {
      toCreate.push(this.createAAN(last12Months.explanation));
    }
    if (last3Months.sufficient_info && !last3Months.passed) {
      toCreate.push(this.createAAN(last3Months.explanation));
    }
    if (foreclosure.sufficient_info && !foreclosure.passed) {
      toCreate.push(this.createAAN(foreclosure.explanation));
    }

    return Promise.all(toCreate);
  }

  onSave() {
    if (confirm("Save and create flags and/or AANs?")) {
      Promise.all([this.createFlags(), this.createAANs()]).then(() => {
        this.reloadApp();
        this.props.onHide();
      });
    }
  }

  toggleExpanded(checkName) {
    const { expanded } = this.state;

    let update;
    if (_.contains(expanded, checkName)) {
      update = _.without(expanded, checkName);
    } else {
      update = _.union(expanded, [checkName]);
    }

    this.setState({
      ...this.state,
      expanded: update,
    });
  }

  getMostRecentDocOfType(app, docType) {
    return _.chain(app.documents)
      .filter((d: any) => d.type === docType)
      .sortBy((d: any) => -1 * parseInt(d.id, 10)) // most recent
      .first()
      .value();
  }

  hasPC() {
    return !!this.props.app.purchase_contract;
  }

  hasPCWithPropertyAddress() {
    const { purchase_contract } = this.props.app;
    return purchase_contract && purchase_contract.propertyAddress;
  }

  getSubjPropReportUrl() {
    const report: any = this.getMostRecentDocOfType(this.props.app, DocTypes.SubjectPropertyReport);
    if (!report) return null;

    return getFileDownloadBaseURL(report.s3_key);
  }

  renderTable() {
    const { subjPropChecks } = this.state;

    return (
      <table className="table table-striped table-hover">
        <thead>
          <tr>
            <th className="col-sm-1" />
            <th className="col-sm-1">Passed</th>
            <th>Consistency check</th>
          </tr>
        </thead>
        <tbody>
          {subjPropChecks.map(chk => {
            const isExpanded = _.contains(this.state.expanded, chk.name);

            const expandedClasses = classnames({
              fa: true,
              "fa-plus-square-o": !isExpanded,
              "fa-minus-square-o": isExpanded,
            });

            let icon;
            if (chk.sufficient_info === false) {
              icon = (
                <div className="subject-property-report__insufficient-info">
                  <i className="fa fa-question" />
                </div>
              );
            } else if (chk.passed) {
              icon = (
                <div className="subject-property-report__passed">
                  <i className="fa fa-check" />
                </div>
              );
            } else {
              icon = (
                <div className="subject-property-report__failed">
                  <i className="fa fa-close" />
                </div>
              );
            }

            return (
              <tr key={chk.name} className="subject-property-report__row">
                <td
                  className="subject-property-report__clickable text-center"
                  onClick={this.toggleExpanded.bind(this, chk.name)}
                >
                  <i className={expandedClasses} />
                </td>
                <td className="text-center">{icon}</td>
                <td>
                  <div className={isExpanded && "subject-property-report__name--expanded"}>
                    {chk.description}
                  </div>
                  {isExpanded && (
                    <div className="subject-property-report__desc">{chk.explanation}</div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  renderStatusWithNote() {
    const { status, override } = this.props.hurdle;

    let effectiveStatus;
    if (override && override.status) {
      effectiveStatus = override.status;
    } else {
      effectiveStatus = status;
    }

    let note;
    if (override && override.status) {
      note = "Status overridden by operator";
    } else if (!this.hasPC()) {
      note = "Waiting for borrower(s) to upload PC";
    } else if (!this.hasPCWithPropertyAddress()) {
      note = "Waiting for operator to ingest property address from PC";
    } else if (status === "red") {
      note = "Some checks have failed";
    } else if (status === "yellow") {
      note = "Insufficient info to run checks";
    } else if (status === "green") {
      note = "All checks have passed";
    } else {
      throw new Error("not reached");
    }

    return (
      <div className="subject-property-report__status">
        <HurdleStatus status={effectiveStatus} />
        <span style={{ marginLeft: "0.75rem" }}>{note}</span>
      </div>
    );
  }

  renderContent() {
    const { fetching, subjPropChecks } = this.state;

    if (subjPropChecks && subjPropChecks.length) {
      return (
        <div>
          <div style={{ marginBottom: 20 }}>
            <a target="_blank" href={this.getSubjPropReportUrl()}>
              <Button kind="tertiary" renderIcon={DocumentView24}>
                Open report PDF in new tab
              </Button>
            </a>
          </div>
          {this.renderTable()}
        </div>
      );
    }

    return (
      <div>
        <Button
          disabled={fetching}
          onClick={this.requestReports.bind(this)}
          renderIcon={CloudDownload16}
        >
          Request subject property report and transaction history
        </Button>
        {fetching && <div style={{ marginTop: 20 }}>Fetching…</div>}
      </div>
    );
  }

  render() {
    const { app, hurdle, onHide } = this.props;

    let footer;
    if (this.needToCreateFlags() || this.needToCreateAANs()) {
      footer = (
        <footer className={styles.footer}>
          <Button kind="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button kind="primary" onClick={this.onSave.bind(this)} renderIcon={Save16}>
            Save changes
          </Button>
        </footer>
      );
    } else {
      footer = <></>;
    }

    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        <div className="subject-property-report__modal-body">
          {this.renderStatusWithNote()}
          {this.hasPCWithPropertyAddress() && this.renderContent()}
        </div>
        {footer}
      </HurdleView>
    );
  }
}

export default connect()(Editor);
